import { Dialog, DialogPanel, Transition, TransitionChild } from '@headlessui/react';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { analytics } from '../utils/analytics';
import { parsePdfStatement, StatementParseResponse, StatementRecord, uploadStatementRecords } from '../services/statementServices';
import { parseOzonStatement } from '../services/parsers/ozonParser';
import IconDownload from './Icon/IconDownload';
import IconLoader from './Icon/IconLoader';
import IconX from './Icon/IconX';
import IconChecks from './Icon/IconChecks';
import _ from 'lodash';
import { Bank, MinimalAccount } from '../global/types';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useUserContext } from '../contexts/UserContext';
import { selectAccounts } from '../store/userDataSlice';
import { storeFileInIndexedDB } from '../utils/fileStorage';
import { showErrorNotification, showSuccessNotification } from '../utils/notificationUtils';
import FileDropzone from './FileDropzone';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import useCurrency from '../hooks/useCurrency';
import { Trans, useTranslation } from 'react-i18next';

export default function UploadReportButton() {
    const [modal, setModal] = useState(false);
    const { t } = useTranslation();

    return (
        <div>
            <button
                type="button"
                onClick={() => setModal(true)}
                className="flex items-center justify-center gap-2 relative p-2 md:px-4 rounded-full bg-white-light/40 dark:bg-dark/40 hover:text-primary hover:bg-white-light/90 dark:hover:bg-dark/60"
            >
                <IconDownload className="block md:hidden" />
                <p className="hidden md:block text-nowrap">{t('upload_statement')}</p>
            </button>
            <UploadDialog isOpen={modal} onClose={() => setModal(false)} />
        </div>
    );
}

const bankKeys: Bank[] = [Bank.TBANK, Bank.SBER, Bank.OZON, Bank.NOT_PRESENT_IN_LIST];

function extractSubCats(transactions: StatementRecord[]) {
    return undefined;
}

function UploadDialog({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
    const { t } = useTranslation();
    const { userId } = useUserContext();
    const { baseCurrency } = useCurrency();
    const navigate = useNavigate();
    const [records, setRecords] = useState<any[] | null>(null);
    const [subCats, setSubCats] = useState<any[] | null>(null);
    const [file, setFile] = useState<File | null>(null);

    // Generate bank options with translations inside component (after i18n is ready)
    const bankOptions = useMemo(() => bankKeys.map(key => {
        let label: string = t('not_present_in_list');
        // Explicit string comparison to avoid type issues if enum is not strictly matched in editor
        if (key === Bank.TBANK) label = t('tinkoff_bank');
        else if (key === Bank.SBER) label = t('sber_bank');
        else if (key === Bank.OZON) label = 'Ozon Bank';

        return { value: key, label };
    }), [t]);

    const accounts = useSelector(selectAccounts) || [];
    const [selectedAccountBaseId, setSelectedAccountId] = useState<string | null>(accounts.length > 0 ? accounts[0].id : null);
    const [bank, setBank] = useState<Bank>(Bank.TBANK);
    const currency = accounts.find((account) => account.id === (selectedAccountBaseId || accounts[0]?.id))?.currency || '';


    const [operationId, setOperationId] = useState<string | null>(null);

    const [processing, setProcessing] = useState<boolean>(false);
    const [uploading, setUploading] = useState<boolean>(false);

    const selectedAccountId = selectedAccountBaseId || accounts[0]?.id;

    useEffect(() => {
        if (!operationId) {
            return;
        }
        const docRef = doc(db, 'bankStatementParseOperations', operationId);

        // Timeout after 30 seconds
        const timeoutId = setTimeout(() => {
            setProcessing(false);
            showErrorNotification(t('us_modal.parsing_timeout'));
        }, 30000);

        const unsubscribe = onSnapshot(
            docRef,
            (snapshot) => {
                if (snapshot.exists()) {
                    const data = snapshot.data();
                    // State is either PROCESSING or COMPLETED or FAILED
                    if (data.status === 'COMPLETED') {
                        clearTimeout(timeoutId);
                        const result = data.result as StatementParseResponse;
                        setSubCats(result.subCategories);
                        setRecords(result.transactions);
                        setProcessing(false);
                        analytics.track('statement_parsed', { operation_id: operationId, transactions_count: result.transactions.length, bank });
                    } else if (data.status === 'FAILED') {
                        clearTimeout(timeoutId);
                        setProcessing(false);
                        showErrorNotification(data.result.errorMessage);
                        console.error(data.result.errorMessage);
                        analytics.track('statement_parsing_failed', { error_code: 'parsing_failed', operation_id: operationId, bank_choice: bank });
                    }
                } else {
                    clearTimeout(timeoutId);
                    setProcessing(false);
                }
            },
            (err) => {
                clearTimeout(timeoutId);
                console.error('Firestore listener error:', err);
                setProcessing(false);
            }
        );

        // Cleanup listener and timeout on unmount
        return () => {
            unsubscribe();
            clearTimeout(timeoutId);
        };
    }, [operationId, t]);

    const handleGenerate = async () => {
        if (file && userId) {
            analytics.track('file_upload_started', { file_type: file.type === 'application/pdf' ? 'pdf' : 'csv'});
            try {
                // Handle "Not present in List" bank option
                if (bank === 'NOT_PRESENT_IN_LIST') {
                    // Store file in IndexedDB for PDF processing
                    const fileKey = await storeFileInIndexedDB(file);

                    // Navigate to PDF mapping page with form details
                    const account = _.find(accounts, { id: selectedAccountId }) as MinimalAccount;
                    const params = new URLSearchParams({
                        accountId: selectedAccountId || '',
                        accountName: account ? account.name : t('unknown'),
                        accountNumber: String(account.number || ''),
                        bank,
                        type: account.type,
                        subType: account.subType || t('unknown'),
                        currency,
                        fileKey,
                    });

                    navigate(`/dashboard/pdf/map-columns?${params.toString()}`);
                    handleClose();
                    return;
                }

                // Handle Ozon Bank directly (Client Side)
                if (bank === Bank.OZON) {
                    setProcessing(true);
                    try {
                        const parsedRecords = await parseOzonStatement(file);
                        if (parsedRecords.length === 0) {
                            throw new Error("No transactions found. Check PDF format.");
                        }
                        setRecords(parsedRecords);
                        setProcessing(false);
                        return; // Skip server logic
                    } catch (e: any) {
                        console.error(e);
                        showErrorNotification("Ozon Parse Error: " + e.message);
                        setProcessing(false);
                        return;
                    }
                }

                // Original TBank/Sber logic
                setProcessing(true);
                setOperationId(null);
                const operationId = await parsePdfStatement(file, userId, bank);

                if (!operationId) {
                    throw new Error(t('us_modal.failed_to_parse_the_statement'));
                }

                analytics.track('statement_parsing_started', { operation_id: operationId, bank_choice: bank });
                setOperationId(operationId);
            } catch (error: any) {
                console.error(error)
                showErrorNotification(error.message);
                setFile(null);
                setProcessing(false);
                analytics.track('file_upload_failed', { file_type: bank, error_code: error.code || error.name || 'unknown' });
            }
        }
    };

    const handleClose = () => {
        setRecords(null);
        setFile(null);
        setSelectedAccountId(null);
        setBank(bankOptions[0].value);
        onClose();
    };

    const canContinue = records ? records.length > 0 && selectedAccountId && bank && currency : false;

    const handleSubmit = async () => {
        if (!canContinue || !userId || !baseCurrency?.code) return;

        setUploading(true);

        try {
            const details = {
                selectedAccountId: selectedAccountId!,
                bank: bank!
            };

            const { pending, rejected } = await uploadStatementRecords(userId, details, records!);
            if (rejected.length > 0) {
                const reasons = [...new Set(rejected.map((r) => r.detail))].slice(0, 3).join(' · ');
                showErrorNotification(t('us_modal.statement_records_rejected', { count: rejected.length, reason: reasons }));
            }
            if (pending > 0) {
                showErrorNotification(t('us_modal.statement_records_pending_rates', { count: pending }));
            }
            analytics.track('statement_imported', {
                bank: bank,
                count: records!.length,
                currency: currency,
            });
            showSuccessNotification(t('us_modal.statement_records_uploaded'));
            handleClose();
            analytics.track('file_upload_completed', { file_type: bank });
        } catch (error: any) {
            showErrorNotification(t('us_modal.failed_to_upload_statement_records') + error.message);
            console.error(error);
            analytics.track('file_upload_failed', { file_type: bank, error_code: error.code || error.name || 'unknown' });
        } finally {
            setUploading(false);
        }
    };

    const processBtnDisabled = processing || uploading || !file || records !== null;

    return (
        <Transition appear show={isOpen} as={Fragment}>
            <Dialog as="div" open={isOpen} onClose={handleClose} className="relative z-50">
                <TransitionChild as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
                    <div className="fixed inset-0 bg-[black]/70 backdrop-blur-sm" />
                </TransitionChild>
                <div className="fixed inset-0 z-[999] overflow-y-auto">
                    <div className="flex items-center justify-center min-h-screen px-4 py-8">
                        <TransitionChild
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <DialogPanel className="panel border-0 p-0 rounded-[32px] overflow-hidden w-full max-w-2xl text-black dark:text-white-dark shadow-2xl bg-white/95 backdrop-blur-md dark:bg-[#121c2c]/95 relative">
                                {/* Processing Overlay */}
                                {processing && (
                                    <div className="absolute inset-0 z-50 flex flex-col items-center justify-center rounded-[32px] bg-white/50 dark:bg-black/50 backdrop-blur-sm">
                                        <span className="animate-spin-tw border-4 border-primary border-l-transparent rounded-full w-12 h-12 mb-4"></span>
                                        <div className="font-semibold text-primary">{t("extracting")}...</div>
                                    </div>
                                )}

                                {/* Header */}
                                <div className="text-xl font-bold bg-white/50 dark:bg-[#121c2c]/50 pl-8 pr-8 py-6 flex justify-between items-center">
                                    <span>{t('us_modal.upload_your_bank_statement')}</span>
                                    <button
                                        onClick={handleClose}
                                        type="button"
                                        className="text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800"
                                    >
                                        <IconX className="w-6 h-6" />
                                    </button>
                                </div>

                                <div className="p-8 max-h-[calc(100vh-200px)] overflow-y-auto no-scrollbar">
                                    {/* Description */}
                                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                                        <Trans
                                            i18nKey="us_modal.please_upload"
                                            components={{
                                                bold: <span className="font-bold text-primary" />
                                            }}
                                        />
                                    </p>

                                    <div className="space-y-6">
                                        {/* Form Fields */}
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                            {/* Account Select */}
                                            <div>
                                                <label htmlFor="account" className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">
                                                    {t('account')}
                                                </label>
                                                <select
                                                    id="account"
                                                    className="form-select w-full h-12 !rounded-2xl border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50 focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                                                    value={selectedAccountId || undefined}
                                                    onChange={(e) => setSelectedAccountId(e.target.value)}
                                                    required
                                                >
                                                    {accounts.map((account) => (
                                                        <option key={account.id} value={account.id}>
                                                            {account.name}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>

                                            {/* Bank Select */}
                                            <div>
                                                <label htmlFor="bank" className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">
                                                    {t('bank')}
                                                </label>
                                                <select
                                                    id="bank"
                                                    className="form-select w-full h-12 !rounded-2xl border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50 focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                                                    value={bank}
                                                    onChange={(e) => setBank(e.target.value as Bank)}
                                                    required
                                                >
                                                    {bankOptions.map((option) => (
                                                        <option key={option.value} value={option.value}>
                                                            {option.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>

                                            <div>
                                                <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">
                                                    {t('currency')}
                                                </label>
                                                <div
                                                    className="form-input w-full h-12 !rounded-2xl border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50 flex items-center text-gray-600 dark:text-gray-300"
                                                    title={t('transaction_currency_follows_account')}
                                                >
                                                    {currency || '—'}
                                                </div>
                                            </div>
                                        </div>

                                        {/* File Dropzone */}
                                        <div>
                                            <FileDropzone
                                                onDrop={(acceptedFile: File) => {
                                                    setFile(acceptedFile);
                                                    setRecords(null);
                                                }}
                                                showFileInstructions={bank === 'NOT_PRESENT_IN_LIST'}
                                            />
                                        </div>

                                        {/* Process Button */}
                                        <button
                                            type="button"
                                            className="btn btn-outline-primary w-full h-12 !rounded-2xl font-semibold border-2 hover:bg-primary hover:text-white transition-all disabled:opacity-50 disabled:pointer-events-none"
                                            disabled={processBtnDisabled}
                                            onClick={handleGenerate}
                                        >
                                            {processing ? `${t("extracting")}...` : t("start")}
                                            {processing && <IconLoader className="animate-spin-tw inline-block align-middle ltr:ml-2 rtl:mr-2 shrink-0" />}
                                        </button>

                                        {/* Results */}
                                        {records && (
                                            <div className="p-4 rounded-2xl bg-success/10 border border-success/20 text-center">
                                                <p className="font-semibold text-success">{t('us_modal.x_records_extracted', { count: records.length })}</p>
                                                {subCats && (
                                                    <p className="font-medium text-success/80 mt-1">
                                                        {t('us_modal.x_new_categories_detected', { count: subCats.filter((s) => s.isNew).length })}
                                                    </p>
                                                )}
                                            </div>
                                        )}

                                        {/* Smart Adjustment Option */}


                                    </div>

                                    {/* Footer Actions */}
                                    <div className="flex justify-end items-center gap-4 mt-8 pt-6 border-t border-gray-100 dark:border-gray-700/50">
                                        <button
                                            type="button"
                                            className="btn btn-outline-danger px-6 h-12 rounded-full font-bold border-2 hover:bg-danger hover:text-white transition-all"
                                            onClick={handleClose}
                                            disabled={uploading}
                                        >
                                            {t('cancel')}
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-primary px-8 h-12 rounded-full font-bold shadow-lg shadow-primary/30 hover:shadow-primary/50 hover:-translate-y-0.5 transition-all disabled:opacity-50"
                                            onClick={handleSubmit}
                                            disabled={!canContinue || uploading}
                                        >
                                            {t('continue')}
                                            {uploading && <IconLoader className="animate-spin-tw inline-block align-middle ltr:ml-2 rtl:mr-2 shrink-0" />}
                                        </button>
                                    </div>
                                </div>
                            </DialogPanel>
                        </TransitionChild>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
}
